import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, UploadSimple, BellSimple, BellSimpleSlash, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import TimelineGrid from '../../components/classroom/TimelineGrid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Teacher timeline — lets the workspace owner upload photos/videos to a
 * collection and see them grouped by day.
 */
const CollectionTimelinePage = () => {
  const { workspaceId, collectionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoUpload = searchParams.get('upload') === '1';

  const [collection, setCollection] = useState(null);
  const [groups, setGroups] = useState([]);
  const [forwardCounts, setForwardCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null); // {succeeded, failed, total}
  const [savingNotify, setSavingNotify] = useState(false);
  const [viewCount, setViewCount] = useState(null);
  const fileRef = useRef(null);

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/collections/${collectionId}/uploads`);
      setGroups(res.data?.groups || []);
      setForwardCounts(res.data?.forward_counts || {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not load timeline');
    }
  }, [collectionId]);

  const fetchViewCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/collections/${collectionId}/views/today`);
      setViewCount(res.data?.count ?? 0);
    } catch {
      // non-fatal
    }
  }, [collectionId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(`/login?redirect=/classroom/${workspaceId}/c/${collectionId}`);
      return;
    }
    (async () => {
      try {
        const c = await axios.get(`${API}/collections/${collectionId}`);
        setCollection(c.data);
      } catch {
        // non-fatal; timeline still loads
      }
      await Promise.all([fetchTimeline(), fetchViewCount()]);
      setLoading(false);
      // If we landed here from a nudge ("Upload to Trip" click), open the
      // native file picker right away — zero friction was the brief.
      if (autoUpload && fileRef.current) {
        setTimeout(() => fileRef.current?.click(), 200);
        // Strip the query param so a refresh doesn't re-open the picker
        setSearchParams({}, { replace: true });
      }
    })();
  }, [user, authLoading, workspaceId, collectionId, navigate, fetchTimeline, fetchViewCount, autoUpload, setSearchParams]);

  const onFiles = async (files) => {
    if (!files || files.length === 0) return;
    const list = Array.from(files);
    setUploading(true);
    setUploadProgress({ succeeded: 0, failed: 0, total: list.length });

    // Upload a SINGLE file with a 90-second timeout + per-file retry on
    // transient failures. Without these guards, a mobile-connection hiccup
    // on file N silently hangs the whole batch forever.
    const uploadOne = async (file) => {
      const fd = new FormData();
      fd.append('file', file);
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const res = await axios.post(
            `${API}/collections/${collectionId}/uploads`, fd,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 90_000,
            },
          );
          return { ok: true, streak: res.data?.streak_days };
        } catch (err) {
          lastErr = err;
          const status = err?.response?.status;
          const isTransient = !status || status >= 500 || err.code === 'ECONNABORTED';
          if (!isTransient || attempt === 2) break;
          await new Promise((r) => setTimeout(r, 600 * (attempt + 1) ** 2));
        }
      }
      return { ok: false, err: lastErr };
    };

    // Concurrency pool of 2 — production R2/PIL throughput tops out faster
    // than dev. 33 photos × 3 workers caused enough load on prod that the
    // backend started returning 5xx for some files; 2 workers is the safe
    // sweet spot (still ~50% faster than sequential).
    const CONCURRENCY = 2;
    let cursor = 0;
    let okCount = 0;
    let failCount = 0;
    let lastStreak = 0;
    const failedFiles = [];
    const worker = async () => {
      while (cursor < list.length) {
        const idx = cursor;
        cursor += 1;
        const r = await uploadOne(list[idx]);
        if (r.ok) {
          okCount += 1;
          if (typeof r.streak === 'number') lastStreak = r.streak;
        } else {
          failCount += 1;
          failedFiles.push(list[idx]);
        }
        setUploadProgress({ succeeded: okCount, failed: failCount, total: list.length });
      }
    };
    await Promise.all(
      Array.from({ length: Math.min(CONCURRENCY, list.length) }, worker),
    );

    setUploading(false);
    setUploadProgress(null);
    if (okCount && !failCount) {
      const streak = lastStreak;
      if (streak && streak >= 2) {
        toast.success(`🔥 Nice — that's ${streak} days in a row.`);
      } else {
        toast.success(
          `✨ Nice — parents will see ${okCount === 1 ? 'this' : `these ${okCount}`} in today's update.`,
        );
      }
    }
    if (failCount) {
      // Loud, persistent toast — the "33 of 33 uploaded" lie was the
      // single biggest data-loss complaint. Be honest and offer a one-tap
      // retry of just the failed files so the teacher doesn't re-upload
      // duplicates of the ones that already landed.
      toast.error(
        okCount
          ? `${okCount} uploaded · ${failCount} failed. Tap to retry the failed ones.`
          : `${failCount} uploads failed — check your connection and try again.`,
        {
          duration: 12000,
          action: failedFiles.length > 0 ? {
            label: `Retry ${failedFiles.length}`,
            onClick: () => onFiles(failedFiles),
          } : undefined,
        },
      );
    }
    fetchTimeline();
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleNotify = async () => {
    if (!collection) return;
    const next = !(collection.notify_parents !== false); // default treats undefined as ON
    setSavingNotify(true);
    try {
      const res = await axios.put(`${API}/collections/${collectionId}/notify`, {
        notify_parents: next,
      });
      setCollection((c) => ({ ...c, notify_parents: res.data.notify_parents }));
      toast.success(
        res.data.notify_parents
          ? 'Parents will be notified about new uploads'
          : 'Parent notifications paused',
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update setting');
    } finally {
      setSavingNotify(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0F1014] text-white">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="collection-timeline-page">
      <div className="max-w-4xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate(`/classroom/${workspaceId}`)}
          className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="timeline-back"
        >
          <ArrowLeft size={14} weight="bold" /> Back to classroom
        </button>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-4">
          <div>
            <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C29D52]">
              Timeline
            </span>
            <h1 className="text-3xl font-black tracking-tight mt-1">
              {collection?.name || 'Recent moments'}
            </h1>
          </div>

          {/* Upload button */}
          <label
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white cursor-pointer transition-all hover:scale-[1.015]"
            style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
            data-testid="timeline-upload-label"
          >
            <UploadSimple size={14} weight="bold" />
            {uploading
              ? (uploadProgress
                  ? (uploadProgress.failed > 0
                      ? `${uploadProgress.succeeded} of ${uploadProgress.total} · ${uploadProgress.failed} failed`
                      : `Uploading ${uploadProgress.succeeded + 1} of ${uploadProgress.total}…`)
                  : 'Uploading…')
              : 'Upload photos / videos'}
            <input
              ref={fileRef}
              type="file"
              accept="image/*,video/*"
              multiple
              hidden
              disabled={uploading}
              onChange={(e) => onFiles(e.target.files)}
              data-testid="timeline-upload-input"
            />
          </label>
        </div>

        {/* Notify-parents toggle + today's view counter */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {collection && (
            <button
              type="button"
              onClick={toggleNotify}
              disabled={savingNotify}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold border transition-all disabled:opacity-60"
              style={{
                borderColor:
                  collection.notify_parents !== false
                    ? 'rgba(194,157,82,0.4)'
                    : 'rgba(255,255,255,0.15)',
                background:
                  collection.notify_parents !== false
                    ? 'rgba(194,157,82,0.08)'
                    : 'transparent',
                color:
                  collection.notify_parents !== false ? '#C29D52' : '#94A3B8',
              }}
              data-testid="timeline-notify-toggle"
            >
              {collection.notify_parents !== false ? (
                <>
                  <BellSimple size={12} weight="fill" />
                  Parents notified daily — Tap to pause
                </>
              ) : (
                <>
                  <BellSimpleSlash size={12} weight="bold" />
                  Notifications paused — Tap to resume
                </>
              )}
            </button>
          )}
          {viewCount !== null && viewCount > 0 && (
            <span
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold border"
              style={{
                borderColor: 'rgba(34,197,94,0.3)',
                background: 'rgba(34,197,94,0.08)',
                color: '#86EFAC',
              }}
              data-testid="timeline-views-today"
            >
              <Eye size={12} weight="fill" />
              {viewCount} {viewCount === 1 ? 'parent' : 'parents'} viewed today
            </span>
          )}
        </div>

        <TimelineGrid groups={groups} editable={false} forwardCounts={forwardCounts} />
      </div>
    </div>
  );
};

export default CollectionTimelinePage;
